import React from 'react';
import { X, Download, Activity, AlertTriangle } from 'lucide-react';
import type { StreamStats, ServerStats } from '../../types';

interface TopBarProps {
  stats: StreamStats;
  serverStats: ServerStats | null;
  timeWindow: '60s' | '5min';
  onTimeWindowChange: (w: '60s' | '5min') => void;
  alerts: string[];   // 告警明细列表（hover chip 显示）
  onExport?: () => void;
  onClose: () => void;
}

const ConnectionDot: React.FC<{ stats: StreamStats }> = ({ stats }) => {
  // 绿/黄/红 状态指示
  let color = 'bg-emerald-500';
  let title = '连接正常';
  if (stats.packetLoss > 3 || stats.rtt > 200) {
    color = 'bg-red-500';
    title = '链路异常';
  } else if (stats.packetLoss > 1 || stats.rtt > 80 || stats.fps < 45) {
    color = 'bg-amber-500';
    title = '链路波动';
  }
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${color} ring-2 ring-white/10`}
      title={title}
    />
  );
};

const TopBar: React.FC<TopBarProps> = ({
  stats,
  serverStats,
  timeWindow,
  onTimeWindowChange,
  alerts,
  onExport,
  onClose,
}) => {
  const dominant = serverStats?.dominant_engine || '?';
  const encoderName = stats.encoderName || dominant;

  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-zinc-900/80 backdrop-blur-md">
      <div className="flex items-center gap-3">
        <Activity size={16} className="text-blue-400" />
        <h2 className="text-sm font-bold tracking-wide text-white">引擎诊断报告 · 完整面板</h2>
        <ConnectionDot stats={stats} />
        <span className="text-xs text-zinc-400 font-mono">{encoderName}</span>
        {alerts.length > 0 && (
          <div className="relative group">
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-[10px] text-amber-300 font-bold cursor-help">
              <AlertTriangle size={10} />
              {alerts.length} 项告警
            </span>
            {/* hover 显示明细 */}
            <div className="absolute top-full left-0 mt-1.5 z-[110] hidden group-hover:block min-w-[200px] rounded-xl bg-zinc-900/95 backdrop-blur border border-amber-500/30 shadow-2xl p-2.5 space-y-1">
              <div className="text-[9px] text-amber-400 uppercase tracking-wide font-bold mb-1 px-1">
                告警明细
              </div>
              {alerts.map((alert, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-1.5 text-[11px] text-zinc-300 px-1.5 py-0.5 rounded hover:bg-white/5"
                >
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span className="font-mono">{alert}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* 时间窗口选择 */}
        <select
          value={timeWindow}
          onChange={(e) => onTimeWindowChange(e.target.value as '60s' | '5min')}
          className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-zinc-300 hover:bg-white/10 cursor-pointer"
        >
          <option value="60s">最近 60 秒</option>
          <option value="5min">最近 5 分钟</option>
        </select>

        {/* 导出 */}
        {onExport && (
          <button
            onClick={onExport}
            title="导出 JSON 快照"
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Download size={14} className="text-zinc-400" />
          </button>
        )}

        {/* 关闭 */}
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
        >
          <X size={16} className="text-zinc-400" />
        </button>
      </div>
    </div>
  );
};

export default TopBar;
